import { useCallback, useEffect, useRef, useState } from 'react';
import { joinStandaloneSession } from '../services/standaloneSessionApi';
import { SessionLiveCheck, standaloneWatchSocket } from '../services/standaloneWatchSocket';
import { coSellerMicService } from '../services/coSellerMicService';
import { MicrophoneUnavailableError } from '../services/microphoneAccess';
import { connectAlertSocketsToRoom, disconnectLiveCallSockets } from '../services/liveCallSockets';
import { clearLiveSessionData, restoreSessionHistory } from '../services/liveSessionData';
import { fetchSessionState } from '../services/sessionStateApi';
import { sessionManager } from '../services/sessionManager';
import { describeBotApiError, getHttpStatus } from '../utils/botApiErrors';
import { parseServerTimestampMs } from '../utils/serverTimestamp';
import { HTTP_CONFLICT, HTTP_FORBIDDEN, HTTP_NOT_FOUND } from '../constants/botApi';
import {
  COSELLER_MIC_COPY,
  STANDALONE_JOIN_MODE,
  StandaloneJoinMode,
  WATCH_SESSION_COPY,
} from '../constants/watchSession';
import type { EndOfCallRequest } from './useEndOfCallFlow';
import type { StandaloneJoin, WatchableSession } from '../types/liveSession';

export interface WatchedStandaloneSession {
  sessionId: string;
  roomId: string;
  sessionName: string;
  ownerName: string | null;
  /** `observer_<n>`, or a co-seller's `salesperson_<n>`. */
  participantId: string;
  /** `salesperson` when the user joined with their mic (a co-seller). */
  role: StandaloneJoinMode;
}

interface UseStandaloneWatcherOptions {
  /** The watched call ended: the page shows its summary (a watcher gets no participant review). */
  onSessionEnded: (request: EndOfCallRequest) => void;
}

/** The service answered a mic join as an observer (an older service ignores `mode`): no mic for this call. */
class MicJoinNotGrantedError extends Error {}

/**
 * Asked before every reconnect of the watcher's socket: the service refuses an
 * observer's reconnect to an ended call at the handshake, so the saved
 * session's `isLive` is how the watcher learns the call ended while it was
 * down. Unknown (no answer, an older backend) counts as live: keep retrying.
 */
const isStandaloneSessionLive: SessionLiveCheck = async (sessionId) => {
  try {
    const state = await fetchSessionState(sessionId);
    return state?.isLive !== false;
  } catch (error) {
    console.warn('[useStandaloneWatcher] Could not check whether the call is live:', error);
    return true;
  }
};

/** The co-seller states' error copy (desktop parity, coseller design §4). */
const describeMicJoinError = (error: unknown): string => {
  if (error instanceof MicrophoneUnavailableError) return COSELLER_MIC_COPY.micAccessNeeded;
  if (error instanceof MicJoinNotGrantedError) return COSELLER_MIC_COPY.micJoinForbidden;
  const status = getHttpStatus(error);
  if (status === HTTP_NOT_FOUND) return COSELLER_MIC_COPY.callEnded;
  if (status === HTTP_CONFLICT) return COSELLER_MIC_COPY.youStartedThisCall;
  if (status === HTTP_FORBIDDEN) return COSELLER_MIC_COPY.micJoinForbidden;
  return describeBotApiError(error, COSELLER_MIC_COPY.micJoinForbidden, COSELLER_MIC_COPY.joinFailed);
};

const toWatchedSession = (join: StandaloneJoin, session: WatchableSession): WatchedStandaloneSession => ({
  sessionId: join.sessionId,
  roomId: join.roomId,
  sessionName: join.sessionName || session.sessionName,
  ownerName: join.ownerName || session.ownerName,
  participantId: join.participantId,
  role: join.role,
});

/**
 * Watch someone's standalone desktop call, read-only, on the same observer
 * path the desktop apps use: join it (POST /api/standalone/sessions/{id}/join
 * returns an `observer_<n>` participant), open `/ws/desktop` as that observer,
 * point production, landmine and competitor at its room, then restore the
 * call so far as silent history. Leaving closes every one of those sockets.
 *
 * Or join it WITH THE USER'S OWN MIC (D6, 2026-10-05), as the desktop apps'
 * co-sellers do: the microphone first (straight from the click, so the browser
 * prompts and the audio starts inside the gesture), then the join with
 * `mode: "salesperson"` (a `salesperson_<n>`), `/ws/desktop` as it, the mic
 * streaming to the proxy (coSellerMicService), the coaching sockets and the
 * history. A co-seller can only leave (D3): Leave says `leave_session` first.
 * The mic is on until they leave: there is no mute (owner, 2026-10-07).
 */
export function useStandaloneWatcher({ onSessionEnded }: UseStandaloneWatcherOptions) {
  const [watchedSession, setWatchedSession] = useState<WatchedStandaloneSession | null>(null);
  const [joiningSessionId, setJoiningSessionId] = useState<string | null>(null);
  const [isJoiningWithMic, setIsJoiningWithMic] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const watchedSessionRef = useRef<WatchedStandaloneSession | null>(null);
  watchedSessionRef.current = watchedSession;
  const onSessionEndedRef = useRef(onSessionEnded);
  onSessionEndedRef.current = onSessionEnded;

  useEffect(
    () =>
      standaloneWatchSocket.onSessionEnded(({ sessionId }) => {
        const watched = watchedSessionRef.current;
        if (!watched || watched.sessionId !== sessionId) return;
        sessionManager.setCallEndTime();
        // Also releases a co-seller's microphone: ended by the owner, the
        // co-seller tears down to the local summary.
        disconnectLiveCallSockets();
        setWatchedSession(null);
        onSessionEndedRef.current({ sessionId, sessionName: watched.sessionName, isCreator: false });
      }),
    []
  );

  const leave = useCallback(() => {
    standaloneWatchSocket.leaveSession();
    disconnectLiveCallSockets();
    clearLiveSessionData();
    setWatchedSession(null);
    setJoinError(null);
  }, []);

  /** Open the session socket, the coaching sockets and the history for a successful join. */
  const attach = useCallback(async (join: StandaloneJoin, session: WatchableSession) => {
    sessionManager.setRoomId(join.roomId);
    const startedAtMs = parseServerTimestampMs(join.startedAt ?? session.startedAt);
    sessionManager.setCallStartTime(startedAtMs !== null ? new Date(startedAtMs) : undefined);
    setWatchedSession(toWatchedSession(join, session));

    await standaloneWatchSocket.connect(
      { sessionId: join.sessionId, participantId: join.participantId, role: join.role },
      isStandaloneSessionLive
    );
    if (join.role === STANDALONE_JOIN_MODE.salesperson) {
      await coSellerMicService.start({ roomId: join.roomId, participantId: join.participantId });
    }
    await connectAlertSocketsToRoom(join.roomId);
    await restoreSessionHistory(join.sessionId);
  }, []);

  /** Resolves true once the watcher is live, false (with `joinError` set) when the join failed. */
  const watch = useCallback(
    async (session: WatchableSession): Promise<boolean> => {
      setJoiningSessionId(session.sessionId);
      setIsJoiningWithMic(false);
      setJoinError(null);
      disconnectLiveCallSockets();
      clearLiveSessionData();
      try {
        await attach(await joinStandaloneSession(session.sessionId), session);
        return true;
      } catch (error) {
        console.error(`[useStandaloneWatcher] Could not watch ${session.sessionId}:`, error);
        disconnectLiveCallSockets();
        setWatchedSession(null);
        setJoinError(describeBotApiError(error, WATCH_SESSION_COPY.joinForbidden, WATCH_SESSION_COPY.joinFailed));
        return false;
      } finally {
        setJoiningSessionId(null);
      }
    },
    [attach]
  );

  /**
   * Join with the user's own mic. Call it straight from the click: the
   * microphone opens before anything is awaited. Resolves true once the
   * co-seller is live, false (with `joinError` set) when it failed; a failure
   * leaves no microphone, socket or roster entry of ours running.
   */
  const joinWithMic = useCallback(
    async (session: WatchableSession, deviceId: string | null, displayName?: string | null): Promise<boolean> => {
      disconnectLiveCallSockets();
      clearLiveSessionData();
      const microphoneReady = coSellerMicService.acquire(deviceId);
      setJoiningSessionId(session.sessionId);
      setIsJoiningWithMic(true);
      setJoinError(null);
      try {
        await microphoneReady;
        const join = await joinStandaloneSession(session.sessionId, {
          mode: STANDALONE_JOIN_MODE.salesperson,
          displayName,
        });
        if (join.role !== STANDALONE_JOIN_MODE.salesperson || !join.captureMic) {
          throw new MicJoinNotGrantedError(`The service joined ${session.sessionId} as ${join.participantId}, not with a mic`);
        }
        await attach(join, session);
        return true;
      } catch (error) {
        console.error(`[useStandaloneWatcher] Could not join ${session.sessionId} with a mic:`, error);
        // An observer the service made of us closes with the rest.
        disconnectLiveCallSockets();
        setWatchedSession(null);
        setJoinError(describeMicJoinError(error));
        return false;
      } finally {
        setJoiningSessionId(null);
        setIsJoiningWithMic(false);
      }
    },
    [attach]
  );

  return {
    watchedSession,
    joiningSessionId,
    isJoiningWithMic,
    joinError,
    watch,
    joinWithMic,
    leave,
  };
}
